import React, { useEffect, useMemo, useState } from 'react'
import { PostTypes } from '~/@types/post'
import {
  AiArtworkSameType,
  AiCreateArtworkModel,
  GetImgToImgModelOptions,
} from '~/graphqls/types'
import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import { to400Image } from '~/utils'
import { MyModal } from '~/components/ui/MyModal'
import { MyBtn } from '~/components/ui/MyBtn'
import { OptionsTabs } from '../ai/OptionsTabs'
import {
  ModelThemeItem,
  ModelThemeOptionsProps,
} from '../ai/ai-form/ModelThemeComp'

import { GET_IMG_TO_IMG_MODEL_OPTIONS } from '~/graphqls/gqls'
import { usePostSocial } from '~/hooks/usePostSocial'
import { message } from 'antd'
import { formatStyleDecorationItem } from '../ai/utils'

const tabs = [
  {
    name: '通用',
    key: `${AiCreateArtworkModel.STABLE_DIFFUSION}`,
    model: AiCreateArtworkModel.STABLE_DIFFUSION,
  },
  {
    name: '漫画',
    key: `${AiCreateArtworkModel.ANIME_DIFFUSION}`,
    model: AiCreateArtworkModel.ANIME_DIFFUSION,
  },
  {
    name: 'MJ',
    key: `${AiCreateArtworkModel.MIDJOURNEY}`,
    model: AiCreateArtworkModel.MIDJOURNEY,
  },
]

interface Props {
  post: PostTypes
  imageId: number
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  showInfo?: () => void
}

const ImageToImageModal = ({
  post,
  imageId,
  visible,
  setVisible,
  showInfo,
}: Props) => {
  const [modelThemeOptions, setModelThemeOptions] = useState<{
    sdModelThemeOptions: ModelThemeOptionsProps[]
    adModelThemeOptions: ModelThemeOptionsProps[]
    mjModelThemeOptions: ModelThemeOptionsProps[]
  }>()
  const [currentModelThemeOptions, setCurrentModelThemeOptions] =
    useState<ModelThemeOptionsProps[]>()
  const [selectModel, setSelectModel] = useState(
    AiCreateArtworkModel.STABLE_DIFFUSION
  )
  const [selectModelCode, setSelectModelCode] = useState()
  const [, setCurrentIndex] = useState(0)

  const getModelThemeOptions = useImperativeQuery<GetImgToImgModelOptions>(
    GET_IMG_TO_IMG_MODEL_OPTIONS
  )

  const { handleSameAgain, canSameAgain } = usePostSocial(post)

  const handleClickOption = (item: any) => {
    setSelectModelCode(item.modelCode)
  }

  useEffect(() => {
    getModelThemeOptions()
      .then(res => {
        const options = res.data.pictureGraph?.aiCreateArtworkOptionMenu
        const sdModelThemeOptions =
          options?.sdModelThemeOptions?.map(item => ({
            ...item,
            name: item?.name || '',
            url: to400Image(item?.url || ''),
            isNew: item?.isNew,
            modelCode: item?.modelCode || 0,
            category: '通用模型',
            model: AiCreateArtworkModel.STABLE_DIFFUSION,
            defaultSelectStyleDecorationOption: formatStyleDecorationItem(
              item?.defaultSelectStyleDecorationOption
            ),
          })) || []
        const adModelThemeOptions =
          options?.adModelThemeOptions?.map(item => ({
            ...item,
            name: item?.name || '',
            url: to400Image(item?.url || ''),
            isNew: item?.isNew,
            modelCode: item?.modelCode || 0,
            category: '漫画模型',
            model: AiCreateArtworkModel.ANIME_DIFFUSION,
            defaultSelectStyleDecorationOption: formatStyleDecorationItem(
              item?.defaultSelectStyleDecorationOption
            ),
          })) || []
        const mjModelThemeOptions =
          options?.mjModelThemeOptions?.map(item => ({
            ...item,
            name: item?.name || '',
            url: to400Image(item?.url || ''),
            isNew: item?.isNew,
            modelCode: item?.modelCode || 0,
            category: 'MJ模型',
            model: AiCreateArtworkModel.MIDJOURNEY,
            tag: null,
            onlyMember: true,
            defaultSelectStyleDecorationOption: formatStyleDecorationItem(
              item?.defaultSelectStyleDecorationOption
            ),
          })) || []

        setModelThemeOptions({
          sdModelThemeOptions,
          adModelThemeOptions,
          mjModelThemeOptions,
        })
      })
      .catch(err => {
        console.log(err)
      })
  }, [])

  useEffect(() => {
    if (selectModel === AiCreateArtworkModel.STABLE_DIFFUSION) {
      setCurrentModelThemeOptions(modelThemeOptions?.sdModelThemeOptions)
    } else if (selectModel === AiCreateArtworkModel.ANIME_DIFFUSION) {
      setCurrentModelThemeOptions(modelThemeOptions?.adModelThemeOptions)
    } else if (selectModel === AiCreateArtworkModel.MIDJOURNEY) {
      setCurrentModelThemeOptions(modelThemeOptions?.mjModelThemeOptions)
    }
  }, [selectModel, modelThemeOptions])

  return (
    <MyModal
      open={visible}
      title={<>选择模型</>}
      onCancel={() => setVisible(false)}
      maskStyle={{
        backgroundColor: 'rgba(0,0,0,0.8)',
      }}
      dark
      width={1440}
      footer={null}
      centered
      isFullScreen
    >
      <OptionsTabs
        options={tabs.map(item => {
          return {
            label: item.name,
          }
        })}
        currentIndex={tabs.findIndex(item => item.model === selectModel)}
        setCurrentIndex={setCurrentIndex}
        onChange={index => setSelectModel(tabs[index].model)}
      />
      <div
        className="flex content-start flex-wrap mb-3 gap-3 min-h-[100px] overflow-auto scrollbar"
        style={{
          height: `calc(100vh - 216px)`, // 216px为其他元素的高度+间距
        }}
      >
        {currentModelThemeOptions?.map(item => {
          const isActive = selectModelCode === item.modelCode
          const isLarge = selectModel === AiCreateArtworkModel.MIDJOURNEY
          return (
            <ModelThemeItem
              key={`${item.name}_${item.modelCode}`}
              isActive={isActive}
              size={isLarge ? 'large' : 'small'}
              item={item}
              onChecked={() => handleClickOption(item)}
            />
          )
        })}
      </div>
      <div className="w-full">
        <MyBtn
          block
          type="primary"
          onClick={() => {
            if (!selectModelCode) {
              message.destroy()
              message.error('请选择模型')
              return
            }
            if (!canSameAgain) {
              return showInfo && showInfo()
            }
            handleSameAgain({
              sameType: AiArtworkSameType.PICTURE_SAME,
              pictureSameModel: selectModelCode,
              imageId: imageId,
              selectModel: selectModel,
            })
          }}
        >
          一键图生图
        </MyBtn>
      </div>
    </MyModal>
  )
}

export default ImageToImageModal
